/**
 * Git Import panel — kicks off the background import job and polls status.
 * Lives inside the Admin page; rendered conditionally.
 */

import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  useGitImportRepos,
  useGitImportStatus,
  useTriggerGitImport,
} from "../../api";

export function GitImportPanel({ onDone }: { onDone: () => void }) {
  const repos = useGitImportRepos();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [since, setSince] = useState<string>("");
  const [maxCount, setMaxCount] = useState<number>(50);

  const trigger = useTriggerGitImport();
  const status = useGitImportStatus({
    refetchInterval: (q) => (q.state.data?.running ? 2000 : false),
  });

  // Default to selecting all repos that exist on disk.
  useEffect(() => {
    if (repos.data && selected.size === 0) {
      setSelected(new Set(repos.data.filter((r) => r.exists).map((r) => r.slug)));
    }
  }, [repos.data, selected.size]);

  // When import flips from running → done, refresh the admin entries list.
  useEffect(() => {
    if (!status.data) return;
    if (!status.data.running && status.data.finished_at) {
      onDone();
    }
  }, [status.data, onDone]);

  const toggle = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const start = () => {
    if (selected.size === 0) return;
    trigger.mutate({
      repos: Array.from(selected),
      since: since || null,
      max_count: maxCount,
    });
  };

  const data = status.data;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h2 className="text-sm font-semibold">Import from git history</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Scans the configured repos&apos; <code>git log</code>, parses conventional commit
        prefixes (<code>feat:</code>, <code>fix:</code>, <code>enhance:</code>),
        and creates a <strong>draft</strong> entry per match. Re-runs are safe — duplicates by commit hash are skipped.
      </p>

      <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex-1">
          <Label className="text-xs">Repositories</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {repos.isLoading && (
              <span className="text-xs text-muted-foreground">Loading…</span>
            )}
            {repos.data?.map((r) => {
              const active = selected.has(r.slug);
              return (
                <button
                  key={r.slug}
                  type="button"
                  onClick={() => toggle(r.slug)}
                  disabled={!r.exists}
                  className={
                    "rounded-full border px-2.5 py-1 text-xs transition-colors " +
                    (active
                      ? "border-brand bg-brand text-white"
                      : "border-border bg-background hover:bg-accent") +
                    (r.exists ? "" : " cursor-not-allowed opacity-50")
                  }
                  title={r.exists ? r.path : `${r.path} (not found on disk)`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="w-full max-w-[160px]">
          <Label className="text-xs" htmlFor="git-import-since">
            Since (YYYY-MM-DD)
          </Label>
          <Input
            id="git-import-since"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            placeholder="optional"
            className="mt-1"
          />
        </div>
        <div className="w-full max-w-[110px]">
          <Label className="text-xs" htmlFor="git-import-max">
            Max commits
          </Label>
          <Input
            id="git-import-max"
            type="number"
            value={maxCount}
            min={1}
            max={1000}
            onChange={(e) => setMaxCount(Number(e.target.value) || 50)}
            className="mt-1"
          />
        </div>
        <Button
          onClick={start}
          disabled={trigger.isPending || data?.running || selected.size === 0}
        >
          {data?.running ? "Importing…" : "Run import"}
        </Button>
      </div>

      {data && (data.running || data.results.length > 0 || data.error) && (
        <div className="mt-4 rounded-md border border-border bg-background p-3 text-xs">
          {data.error && (
            <p className="text-destructive">Error: {data.error}</p>
          )}
          {data.running && (
            <p className="text-muted-foreground">
              Import running… started {formatRelative(data.started_at)}
            </p>
          )}
          {!data.running && data.results.length > 0 && (
            <>
              <p className="mb-2 font-medium">
                Done {formatRelative(data.finished_at)} —{" "}
                {data.results.reduce((n, r) => n + r.imported, 0)} entries created
              </p>
              <ul className="space-y-1">
                {data.results.map((r) => (
                  <li key={r.repo} className="flex flex-wrap gap-3 text-muted-foreground">
                    <span className="font-mono text-foreground">{r.repo}</span>
                    <span>
                      {r.imported} new · {r.skipped_duplicate} duplicates ·{" "}
                      {r.skipped_uncategorized} skipped · {r.errors.length} errors
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
